import { useState, useEffect } from 'react';
import { Send, User, Clock, Wallet, AlertTriangle, Package } from 'lucide-react';
import { transferApi, settingsApi, userApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

export default function UserTransfer() {
  const { user, refreshUser } = useAuth();
  const [settings, setSettings] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [sentTransfers, setSentTransfers] = useState([]);
  const [receivedTransfers, setReceivedTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [amount, setAmount] = useState('');
  const [password, setPassword] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [settingsRes, transfersRes, dashboardRes] = await Promise.all([
          settingsApi.get(),
          transferApi.getAll(),
          userApi.getDashboard()
        ]);
        setSettings(settingsRes.data.settings);
        setSentTransfers(transfersRes.data.sent || []);
        setReceivedTransfers(transfersRes.data.received || []);
        setDashboard(dashboardRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const amountNum = parseFloat(amount);
    const feePercent = settings?.transfer_fee || 2;
    const totalDeduction = amountNum + (amountNum * feePercent / 100);
    const cashWalletBalance = dashboard?.cash_wallet || 0;

    if (totalDeduction > cashWalletBalance) {
      toast.error(`Insufficient Cash Wallet balance. Available: $${cashWalletBalance.toFixed(2)}, Required: $${totalDeduction.toFixed(2)}`);
      return;
    }

    setSubmitting(true);
    try {
      const response = await transferApi.create({
        recipient_email: recipientEmail,
        amount: amountNum,
        password: password
      });
      toast.success('Transfer successful! Fee: $' + response.data.fee.toFixed(2));
      setRecipientEmail('');
      setAmount('');
      setPassword('');
      refreshUser();
      // Refetch transfers and dashboard
      const [transfersRes, dashboardRes] = await Promise.all([
        transferApi.getAll(),
        userApi.getDashboard()
      ]);
      setSentTransfers(transfersRes.data.sent || []);
      setReceivedTransfers(transfersRes.data.received || []);
      setDashboard(dashboardRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Transfer failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-amber-500/20 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  const feePercent = settings?.transfer_fee || 2;
  const feeAmount = amount ? parseFloat(amount) * (feePercent / 100) : 0;
  const cashWalletBalance = dashboard?.cash_wallet || 0;
  const depositWalletBalance = dashboard?.deposit_wallet || 0;
  const blcPrice = settings?.blc_price || 0.027;
  const usdToBLC = (usd) => blcPrice > 0 ? (Math.abs(usd) / blcPrice).toFixed(2) : '0.00';

  return (
    <div className="max-w-4xl mx-auto space-y-8" data-testid="transfer-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 rounded-xl bg-purple-500/10">
          <Send className="w-6 h-6 text-purple-400" />
        </div>
        <div>
          <h1 className="text-2xl text-white font-medium">P2P Transfer</h1>
          <p className="text-sm text-zinc-500">Send funds to other users from your Cash Wallet</p>
        </div>
      </div>

      {/* Important Notice */}
      <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-amber-400 font-medium text-sm">Important: P2P Transfer Rules</p>
            <ul className="text-xs text-amber-200 mt-1 space-y-1">
              <li>• Transfers are sent from your <strong>Cash Wallet</strong></li>
              <li>• Recipient receives funds in their <strong>Deposit Wallet</strong></li>
              <li>• Received P2P funds can <strong>ONLY be used for staking</strong> - NOT withdrawable</li>
              <li>• Transfer fee ({feePercent}%) is charged on each transfer</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-4">
          {/* Wallet Balances */}
          <div className="glass-card p-6">
            <h3 className="text-white font-medium mb-4">Your Wallets</h3>
            <div className="space-y-3">
              <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Wallet className="w-4 h-4 text-green-400" />
                    <span className="text-sm text-zinc-400">Cash Wallet</span>
                  </div>
                  <span className="text-green-400 font-medium">${cashWalletBalance.toFixed(2)}</span>
                </div>
                <p className="text-[10px] text-green-300 mt-1">Source for P2P transfers | {usdToBLC(cashWalletBalance)} BLC</p>
              </div>
              <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Package className="w-4 h-4 text-amber-400" />
                    <span className="text-sm text-zinc-400">Deposit Wallet</span>
                  </div>
                  <span className="text-amber-400 font-medium">${depositWalletBalance.toFixed(2)}</span>
                </div>
                <p className="text-[10px] text-amber-300 mt-1">Received P2P funds go here | {usdToBLC(depositWalletBalance)} BLC</p>
              </div>
            </div>
          </div>

          <div className="glass-card p-6">
            <h3 className="text-white font-medium mb-3">Transfer Rules</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-zinc-400">Transfer Fee</span>
                <span className="text-amber-400">{feePercent}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-400">Processing</span>
                <span className="text-green-400">Instant</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-400">Source Wallet</span>
                <span className="text-purple-400">Cash Wallet</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-400">Destination</span>
                <span className="text-amber-400">Deposit Wallet</span>
              </div>
            </div>
          </div>
        </div>

        <div className="glass-card p-6">
          <h2 className="text-white font-medium mb-4">Send Funds</h2>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-xs text-zinc-400 mb-2 block">Recipient Email</label>
              <Input
                type="email"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                placeholder="user@example.com"
                className="bg-black/50 border-white/10 text-white"
                data-testid="transfer-email"
                required
              />
            </div>

            <div>
              <label className="text-xs text-zinc-400 mb-2 block">Amount (USDT)</label>
              <Input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Enter amount in USDT"
                className="bg-black/50 border-white/10 text-white"
                data-testid="transfer-amount"
                required
              />
              {amount && parseFloat(amount) > 0 && (
                <p className="text-xs text-amber-400 mt-1.5">
                  <span className="text-zinc-500">Equivalent:</span> {usdToBLC(parseFloat(amount))} BLC
                </p>
              )}
            </div>

            <div>
              <label className="text-xs text-zinc-400 mb-2 block">Your Password</label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                className="bg-black/50 border-white/10 text-white"
                data-testid="transfer-password"
                required
              />
            </div>

            {amount && (
              <div className="p-4 rounded-lg bg-black/30 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Transfer Amount</span>
                  <div className="text-right">
                    <span className="text-white">${parseFloat(amount).toFixed(2)}</span>
                    <span className="text-amber-400 text-xs ml-1">({usdToBLC(parseFloat(amount))} BLC)</span>
                  </div>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Fee ({feePercent}%)</span>
                  <span className="text-red-400">+${feeAmount.toFixed(2)}</span>
                </div>
                <div className="border-t border-white/10 pt-2 flex justify-between text-sm">
                  <span className="text-zinc-400">Total from Cash Wallet</span>
                  <div className="text-right">
                    <span className="text-white font-medium">${(parseFloat(amount) + feeAmount).toFixed(2)}</span>
                    <span className="text-amber-400 text-xs ml-1">({usdToBLC(parseFloat(amount) + feeAmount)} BLC)</span>
                  </div>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Recipient Receives</span>
                  <div className="text-right">
                    <span className="text-green-400 font-medium">${parseFloat(amount).toFixed(2)}</span>
                    <span className="text-green-400/70 text-xs ml-1">({usdToBLC(parseFloat(amount))} BLC)</span>
                  </div>
                </div>
                <div className="border-t border-white/10 pt-2 flex justify-between text-sm">
                  <span className="text-zinc-400">BLC Rate</span>
                  <span className="text-amber-400">${blcPrice} per BLC</span>
                </div>
              </div>
            )}

            <Button
              type="submit"
              disabled={submitting}
              className="w-full gold-btn"
              data-testid="submit-transfer"
            >
              {submitting ? (
                <div className="w-5 h-5 border-2 border-black/20 border-t-black rounded-full animate-spin" />
              ) : (
                'Send Transfer'
              )}
            </Button>
          </form>
        </div>
      </div>

      <div className="glass-card p-6">
        <h2 className="text-white font-medium mb-4">Transfer History</h2>
        
        {sentTransfers.length === 0 && receivedTransfers.length === 0 ? (
          <div className="text-center py-8">
            <Clock className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
            <p className="text-zinc-500">No transfers yet</p>
          </div>
        ) : (
          <div className="space-y-4">
            {sentTransfers.length > 0 && (
              <div>
                <h3 className="text-sm text-zinc-400 mb-2">Sent (From Cash Wallet)</h3>
                <div className="space-y-2">
                  {sentTransfers.map((t) => (
                    <div key={t.id} className="flex items-center justify-between p-3 rounded-lg bg-black/30">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-red-500/10 flex items-center justify-center">
                          <Send className="w-4 h-4 text-red-400" />
                        </div>
                        <div>
                          <p className="text-white text-sm">{t.recipient_email}</p>
                          <p className="text-xs text-zinc-500">
                            {new Date(t.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-red-400 font-medium">-${(t.amount + t.fee).toFixed(2)}</p>
                        <p className="text-xs text-amber-400">{usdToBLC(t.amount + t.fee)} BLC</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {receivedTransfers.length > 0 && (
              <div>
                <h3 className="text-sm text-zinc-400 mb-2">Received (Added to Deposit Wallet - Staking Only)</h3>
                <div className="space-y-2">
                  {receivedTransfers.map((t) => (
                    <div key={t.id} className="flex items-center justify-between p-3 rounded-lg bg-black/30">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-green-500/10 flex items-center justify-center">
                          <User className="w-4 h-4 text-green-400" />
                        </div>
                        <div>
                          <p className="text-white text-sm">P2P Transfer Received</p>
                          <p className="text-xs text-zinc-500">
                            {new Date(t.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-green-400 font-medium">+${t.amount.toFixed(2)}</p>
                        <p className="text-xs text-amber-400">{usdToBLC(t.amount)} BLC</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
